import {React, useState, useEffect, useRef} from 'react';

import styles from './players.module.css'
import constants from '../../../constants.js'
import api from '../../../routes/api.js'
import utils from '../../../utils/utils.js';

import { useSidebarProvider } from '../../../providers/SidebarProvider.js';
import { useApiProvider } from '../../../providers/ApiProvider.js';
import { useLeagueTypeProvider } from '../../../providers/LeagueTypeProvider.js';

import InfoDialog from '../../common/InfoDialog/InfoDialog.js';
import Table from "../../common/Table/Table.js"
import Switch from "../../common/Switch/Switch.js"
import Select from "../../common/Select/Select.js"



const Players = ({setSelectedMenuItem, selectedLocation, selectedDivision}) => {
  const { setIsSidebarShowing } = useSidebarProvider();
  const { setDialog, isLoading, setIsLoading, LoadingSpinner} = useApiProvider();
    const { leagueType } = useLeagueTypeProvider();
  

  const [width, setWidth] = useState(window.innerWidth);

  const [seasonPlayerList, setSeasonPlayerList] = useState([]);
  const [careerPlayerList, setCareerPlayerList] = useState([]);
  const [filteredPlayerList, setFilteredPlayerList] = useState([]);
  // Default to season stats
  const [isCareerStats, setIsCareerStats] = useState(false);
  const [genderFilter, setGenderFilter] = useState(constants.all);
  const [infoDialogText, setInfoDialogText] = useState({
    title: "",
    body: ""
  });

  const infoDialogRef = useRef(null);

  const openInfoDialog = () => {
    if (infoDialogRef.current){
      infoDialogRef.current.style.display = 'flex';
      infoDialogRef.current.showModal();
    }
  }

  const closeInfoDialog = () => {
    if (infoDialogRef.current){
      infoDialogRef.current.style.display = 'none';
      infoDialogRef.current.close();
    }
  }

  const onStatsChange = async () => {
    // We only want to get the info when the user selects it since it's a lot, so get it on the first load
    let updatedCareerList = careerPlayerList;
    if (careerPlayerList.length === 0) {

      updatedCareerList = await getCareerStats();
    }
    // Negate cause we are flipping it, and it defaults to false
    setFilteredPlayerList(filterPlayers(genderFilter, !isCareerStats, updatedCareerList));
    setIsCareerStats(prev => !prev);
  }

  const onGenderChange = (e) => {
    const newGender = e.currentTarget.value;
     setGenderFilter(newGender);
     // isCareerStats isn't changing here so just pass in the state
     setFilteredPlayerList(filterPlayers(newGender, isCareerStats));
  }

  /* This takes in the new state since this is also changing before re-render*/
  const filterPlayers = (gender, isCareer, latestCareerList = careerPlayerList) => {
    // deep-copy here because seasonPlayerList is state
    let filteredList = isCareer ? latestCareerList : seasonPlayerList.map(item => ({ ...item }));

    if (gender != constants.all) {
      filteredList = filteredList.filter((value) => {
        return value[constants.playerStats.gender] == gender;
      })
    }

    return filteredList;
  }

  const getCareerStats = async () => {
    setIsLoading(true);
    try {
      const baseUrl = utils.getBaseUrl(leagueType);

      const careerResponse = await api.fetch(baseUrl, constants.uri.players(selectedDivision[constants.divisionId]), constants.uri.careerQuery);
      if (!careerResponse.hasOwnProperty([constants.values])) throw careerResponse;

      const careerPlayers = careerResponse[constants.values].map(p => ({
        ...p,
        [constants.fullName]: p[constants.firstName] + " " + p[constants.lastName],
        [constants.teamName]: p[constants.teamName] != constants.multipleTeams ? 
          (p[constants.clubLogo] ? <div style={{display:'flex', flexDirection:'row', justifyContent:'flex-start', alignItems:'center', gap:'10px'}}>
              <img src={`/logos/${p[constants.clubLogo]}.png`} style={{height:'32px', width: '32px'}}/>
              {p[constants.teamName]}
            </div> : p[constants.teamName]) : constants.multipleTeams
      })).sort((a, b) => 
        b[constants.wins] - a[constants.wins] || 
        b[constants.pointsPerGame] - a[constants.pointsPerGame]
      );

      setCareerPlayerList(careerPlayers);
      setIsLoading(false);

      return careerPlayers;
    }
    catch (err) {
      console.error(err);
      setDialog({
        title: err.title ? err.title : constants.messages.errorMessage, 
        code: err.code ? err.code : "N/A", 
        message: err.message, 
        isError: true});
      setIsLoading(false);
    }
  }

  const getTableHeaders = () => {
    if (leagueType === constants.pages.genderLeagues){
      return constants.table.genderLeaguePlayerCols(setInfoDialogText, openInfoDialog)
    }
    if (genderFilter == constants.all) {
      return isCareerStats ? constants.table.careerCols(setInfoDialogText, openInfoDialog) : constants.table.playerCols(setInfoDialogText, openInfoDialog);
    }
    else {
      return isCareerStats ? constants.table.genderedCareerCols(setInfoDialogText, openInfoDialog) : constants.table.genderedPlayerCols(setInfoDialogText, openInfoDialog);
    }
  }

  useEffect(() => {
    if (isLoading) return; // Wait for data from App.js
    
    const getPlayerLists = async () => {
      setIsLoading(true);
      try {
        const baseUrl = utils.getBaseUrl(leagueType);

        const seasonResponse = await api.fetch(baseUrl, constants.uri.players(selectedDivision[constants.divisionId]));
        if (!seasonResponse.hasOwnProperty([constants.values])) throw seasonResponse;

        const seasonPlayers = seasonResponse[constants.values].map(p => ({
          ...p,
          [constants.fullName]: (
            <div style={{display:'flex', flexDirection:'row', justifyContent:'flex-start', alignItems:'center', gap:'10px'}}>
              <img src={`/logos/${p[constants.clubLogo]}.png`} style={{height:'32px', width: '32px'}}/>
              {p[constants.firstName] + " " + p[constants.lastName]}
              {utils.getPlayerStatusIcon(p)}
            </div>)
        }));

        seasonPlayers.sort((a, b) => {
          if (a[constants.ranking] < b[constants.ranking]) {
            return -1;
          }
          if (a[constants.ranking] > b[constants.ranking]) {
            return 1;
          }
          return 0;
        });
        
        setSeasonPlayerList(seasonPlayers);
        setFilteredPlayerList(seasonPlayers);
        // setCareerPlayerList(careerPlayers);
        setIsLoading(false);
      } 
      catch (err) {
        console.error(err);
        setDialog({
          title: err.title ? err.title : constants.messages.errorMessage, 
          code: err.code ? err.code : "N/A", 
          message: err.message, 
          isError: true});
        setIsLoading(false);
      }
    }

    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);

    // If the page is loading on a small width, hide the sidebar so you see the page
    if (window.innerWidth < 1300){
      setIsSidebarShowing(false);
    }

    setSelectedMenuItem(constants.sidebarItems[3]);

    // Always default filters to avoid logic complications, and for UX
    setIsCareerStats(false);
    setCareerPlayerList([]);
    setGenderFilter(constants.all);

    if (selectedDivision[constants.divisionId] != "") getPlayerLists();

    return () => window.removeEventListener("resize", handleResize);
  }, [selectedDivision])

  return (
    <div className={styles.Players}>
      <InfoDialog ref={infoDialogRef}
                  dialogTitle={infoDialogText.title}
                  dialogMessage={infoDialogText.body}
                  closeDialog={closeInfoDialog}/>

      <div className={styles.Header}>
        <div className={styles.Title}>{constants.titles.playerStats}</div>
        <div className={styles.SubTitle}> {`${selectedLocation[constants.locationName]} ${selectedDivision[constants.divisionName]} DIVISION`}</div>        

        {!isLoading ? <>
          <div className={styles.TableFilters}>
            <div className={styles.GenderSelect}>
              <Select disabled={false} onChange={onGenderChange} additionalStyle={styles.Select} currentValue={genderFilter} options={constants.genderSelect}/>
            </div>
              
            {/* No career stats for local/gender league yet */}
            {leagueType === constants.pages.leagues ? 
              <div className={styles.StatsToggle}>
                {/* isOn is true if the stats toggle is set to season. False if set to career */}
                <Switch isOn={!isCareerStats} handleToggle={onStatsChange} leftText={constants.playerStats.season} rightText={constants.playerStats.career}/>
              </div> : <></>}
          </div>
        </> : <></>} 
      </div>
      
      {!isLoading ? 
      <div className={styles.TableContainer}>
        <Table 
          headers={getTableHeaders()}
          defaultHeader={isCareerStats ? constants.wins : constants.ranking}
          tableKeys={[constants.playerId, constants.teamId]} 
          tableData={filteredPlayerList} 
          setTableData={setFilteredPlayerList}
          canSort={true}/>
      </div>
      : <LoadingSpinner/>}

    </div>
  )
}

export default Players;